import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/DashboardLayout";
import { KCard } from "@/components/KCard";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Save } from "lucide-react";
import { toast } from "sonner";
import {
  fetchSettings,
  updateSettings,
  formatApiError,
} from "@/api/userAccount";

const TIMEZONES = [
  "UTC",
  "America/New_York",
  "America/Los_Angeles",
  "Europe/London",
  "Europe/Paris",
  "Asia/Tokyo",
  "Asia/Dhaka",
  "Australia/Sydney",
];

export default function SettingsPage() {
  const queryClient = useQueryClient();

  const settingsQuery = useQuery({
    queryKey: ["settings"],
    queryFn: fetchSettings,
  });

  const [darkMode, setDarkMode] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [desktopNotifications, setDesktopNotifications] = useState(true);
  const [newConversationAlerts, setNewConversationAlerts] = useState(true);
  const [timezone, setTimezone] = useState("UTC");

  useEffect(() => {
    const s = settingsQuery.data?.settings;
    if (s) {
      setDarkMode(s.darkMode);
      setEmailNotifications(s.emailNotifications);
      setDesktopNotifications(s.desktopNotifications);
      setNewConversationAlerts(s.newConversationAlerts);
      setTimezone(s.timezone || "UTC");
      document.documentElement.classList.toggle("dark", s.darkMode);
    }
  }, [settingsQuery.data]);

  const updateSettingsMutation = useMutation({
    mutationFn: updateSettings,
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["settings"] });
      if (vars.darkMode !== undefined) {
        document.documentElement.classList.toggle("dark", !!vars.darkMode);
      }
      toast.success("System settings saved");
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const onSavePreferences = () => {
    updateSettingsMutation.mutate({
      darkMode,
      emailNotifications,
      desktopNotifications,
      newConversationAlerts,
      timezone,
    });
  };
  const loading = settingsQuery.isPending;

  return (
    <DashboardLayout>
      <div className="dash-page max-w-3xl">
        <div className="dash-page-header">
          <div>
            <h1 className="dash-page-title">System Settings</h1>
            <p className="dash-page-subtitle">Admin-only global configuration area.</p>
          </div>
        </div>

        <KCard className="p-6 space-y-6">
          {loading ? (
            <Skeleton className="h-32 w-full" />
          ) : (
            <>
              <div className="flex items-center justify-between py-3 border-b border-border">
                <div>
                  <p className="font-medium text-foreground">Dark mode</p>
                  <p className="text-sm text-muted-foreground">Saved to your account</p>
                </div>
                <Switch
                  checked={darkMode}
                  onCheckedChange={(v) => {
                    setDarkMode(v);
                    document.documentElement.classList.toggle("dark", v);
                  }}
                />
              </div>
              <div className="flex items-center justify-between py-3 border-b border-border">
                <div>
                  <p className="font-medium text-foreground">Email notifications</p>
                  <p className="text-sm text-muted-foreground">Course and account updates</p>
                </div>
                <Switch checked={emailNotifications} onCheckedChange={setEmailNotifications} />
              </div>
              <div className="flex items-center justify-between py-3 border-b border-border">
                <div>
                  <p className="font-medium text-foreground">Desktop notifications</p>
                </div>
                <Switch checked={desktopNotifications} onCheckedChange={setDesktopNotifications} />
              </div>
              <div className="flex items-center justify-between py-3 border-b border-border">
                <div>
                  <p className="font-medium text-foreground">New conversation alerts</p>
                </div>
                <Switch checked={newConversationAlerts} onCheckedChange={setNewConversationAlerts} />
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-3">
                <div>
                  <p className="font-medium text-foreground">Timezone</p>
                </div>
                <select
                  className="h-10 px-3 rounded-lg border border-border bg-background text-sm text-foreground min-w-[220px]"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                >
                  {TIMEZONES.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end pt-2">
                <Button onClick={onSavePreferences} disabled={updateSettingsMutation.isPending}>
                  <Save size={16} /> {updateSettingsMutation.isPending ? "Saving..." : "Save system settings"}
                </Button>
              </div>
            </>
          )}
        </KCard>
      </div>
    </DashboardLayout>
  );
}
